import React, { useMemo } from "react"
import {
  Table,
  Typography,
  Button,
  Dropdown,
  Tag,
  message,
} from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import { MoreOutlined, DeleteOutlined } from "@mimber/ui-web"
import { Modal } from "antd"
import {
  useMemberPermissions,
  useRevokeMemberPermission,
} from "@mimber/client/mimber-organization"
import type { UserPermissionDto } from "@mimber/schema"
import type { MemberDirectPermissionsTableProps } from "./MemberDirectPermissionsTable.types"

const { Text } = Typography

export const MemberDirectPermissionsTable: React.FunctionComponent<
  MemberDirectPermissionsTableProps
> = ({ memberUuid, className }) => {
  const { data: response, isLoading, error } = useMemberPermissions(memberUuid)
  const [modal, contextHolder] = Modal.useModal()

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load permissions: ${error.message}`)
    }
  }, [error])

  const revokeMutation = useRevokeMemberPermission(memberUuid, {
    onSuccess: () => {
      message.success("Permission revoked successfully")
    },
    onError: (err) => {
      message.error(`Failed to revoke permission: ${err.message}`)
    },
  })

  const handleRevoke = (record: UserPermissionDto) => {
    modal.confirm({
      title: "Revoke Permission",
      content: `Are you sure you want to revoke the ${record.resource} / ${record.action} permission?`,
      okText: "Revoke",
      okType: "primary",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          revokeMutation.mutate(
            { permissionUuid: record.uuid },
            {
              onSuccess: () => resolve(),
              onError: () => reject(),
            }
          )
        }),
    })
  }

  const columns: ColumnsType<UserPermissionDto> = useMemo(
    () => [
      {
        title: "Resource",
        key: "resource",
        render: (_, record) => <Tag>{record.resource.replace(/_/g, " ")}</Tag>,
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => <Text>{record.action}</Text>,
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "revoke",
                    label: "Revoke",
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleRevoke(record),
                  },
                ],
              }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [handleRevoke]
  )

  return (
    <>
      {contextHolder}
      <Table<UserPermissionDto>
        bordered
        columns={columns}
        dataSource={response?.data ?? []}
        loading={isLoading}
        rowKey="uuid"
        pagination={false}
        className={className}
        locale={{ emptyText: "No direct permissions granted" }}
      />
    </>
  )
}
